import { z } from "zod";
import { ExtentSchema } from "./map";
import { AssetVersionIdSchema, HexColorSchema, IdSchema, listOf, NameSchema } from "./primitives";
import { SceneSchema } from "./timeline";

/** Someone other than the perspective taking part in a thread, voiced by the game master. */
export const ThreadMemberSchema = z.strictObject({
  id: IdSchema,
  name: NameSchema,
  role: NameSchema.optional(),
  image: AssetVersionIdSchema.optional(),
  color: HexColorSchema.optional(),
});
export type ThreadMember = z.infer<typeof ThreadMemberSchema>;

/**
 * Who speaks in a thread: the perspective itself, one of the thread's members, or someone
 * described in place.
 */
export const ThreadSpeakerSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("perspective") }),
  z.strictObject({ type: z.literal("member"), memberId: IdSchema }),
  z.strictObject({
    type: z.literal("custom"),
    name: NameSchema,
    role: NameSchema.optional(),
    image: AssetVersionIdSchema.optional(),
    color: HexColorSchema.optional(),
  }),
]);
export type ThreadSpeaker = z.infer<typeof ThreadSpeakerSchema>;

/** One key per speaker, so each counts once however often they respond or react. */
export function speakerKey(speaker: ThreadSpeaker): string {
  switch (speaker.type) {
    case "perspective":
      return "perspective";
    case "member":
      return `member:${speaker.memberId}`;
    case "custom":
      return `custom:${speaker.name}`;
  }
}

export const ThreadEntryPresentationSchema = z.union([
  ThreadSpeakerSchema,
  z.strictObject({ type: z.literal("system") }),
]);
export type ThreadEntryPresentation = z.infer<typeof ThreadEntryPresentationSchema>;

export const PollOptionSchema = z.strictObject({ id: IdSchema, label: NameSchema });
export type PollOption = z.infer<typeof PollOptionSchema>;

/** Something the perspective asks of a thread. The game master decides whether it happens. */
export const ThreadRequestSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("askIn"), name: NameSchema }),
  z.strictObject({ kind: z.literal("askOut"), speaker: ThreadSpeakerSchema }),
  z.strictObject({ kind: z.literal("rename"), title: NameSchema }),
]);
export type ThreadRequest = z.infer<typeof ThreadRequestSchema>;

/** How many threads a perspective may pin. */
export const MAX_PINNED_THREADS = 4;

export const ThreadEntryContentSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("text"), text: z.string().max(100_000) }),
  z.strictObject({
    type: z.literal("poll"),
    pollId: IdSchema,
    question: z.string().trim().min(1).max(20_000),
    options: listOf(PollOptionSchema)
      .min(2)
      .refine(
        options => new Set(options.map(option => option.id)).size === options.length,
        "Poll option ids must be unique.",
      ),
    freeform: z
      .boolean()
      .optional()
      .meta({ description: "Respondents may answer in their own words as well as choose." }),
  }),
  z.strictObject({ type: z.literal("request"), request: ThreadRequestSchema }),
]);
export type ThreadEntryContent = z.infer<typeof ThreadEntryContentSchema>;

export const ThreadEntrySchema = z.strictObject({
  id: IdSchema,
  presentation: ThreadEntryPresentationSchema,
  content: ThreadEntryContentSchema,
  /** A scene the message shows, played from it. */
  scene: SceneSchema.optional(),
});
export type ThreadEntry = z.infer<typeof ThreadEntrySchema>;

export const ThreadStatusSchema = z.enum(["open", "archived"]);
export type ThreadStatus = z.infer<typeof ThreadStatusSchema>;

/** The marks a picker offers before anyone types, grouped and named. Any emoji is a valid mark. */
export const SUGGESTED_REACTIONS: { name: string; marks: { mark: string; name: string }[] }[] = [
  {
    name: "Faces",
    marks: [
      { mark: "😂", name: "laughing" },
      { mark: "🤣", name: "howling" },
      { mark: "😀", name: "grinning" },
      { mark: "🙂", name: "pleased" },
      { mark: "😉", name: "winking" },
      { mark: "😊", name: "warm" },
      { mark: "🥲", name: "holding on" },
      { mark: "🤔", name: "thinking" },
      { mark: "😬", name: "grimacing" },
      { mark: "😐", name: "flat" },
      { mark: "🙄", name: "unimpressed" },
      { mark: "😮", name: "astonished" },
      { mark: "😴", name: "asleep" },
      { mark: "😭", name: "sobbing" },
      { mark: "😡", name: "furious" },
      { mark: "🤯", name: "stunned" },
    ],
  },
  {
    name: "Hands",
    marks: [
      { mark: "👍", name: "agreed" },
      { mark: "👎", name: "against" },
      { mark: "🫡", name: "saluting" },
      { mark: "👏", name: "applause" },
      { mark: "🙌", name: "raised" },
      { mark: "🤝", name: "handshake" },
      { mark: "🙏", name: "please" },
      { mark: "✊", name: "resolve" },
      { mark: "✌️", name: "peace" },
      { mark: "🤞", name: "hoping" },
      { mark: "👌", name: "exactly" },
      { mark: "🤌", name: "precisely" },
      { mark: "👊", name: "fist" },
      { mark: "🖐️", name: "halt" },
      { mark: "👆", name: "the line above" },
      { mark: "👇", name: "the line below" },
    ],
  },
  {
    name: "Feeling",
    marks: [
      { mark: "❤️", name: "love" },
      { mark: "🖤", name: "grief" },
      { mark: "💔", name: "broken" },
      { mark: "🔥", name: "fire" },
      { mark: "✨", name: "brilliant" },
      { mark: "💯", name: "a hundred" },
      { mark: "🎉", name: "celebration" },
      { mark: "🥂", name: "a toast" },
    ],
  },
  {
    name: "War",
    marks: [
      { mark: "⚔️", name: "battle" },
      { mark: "🛡️", name: "defence" },
      { mark: "🏰", name: "fortress" },
      { mark: "🎖️", name: "decoration" },
      { mark: "🪖", name: "helmet" },
      { mark: "🚩", name: "flag" },
      { mark: "🏳️", name: "surrender" },
      { mark: "🗡️", name: "dagger" },
      { mark: "💣", name: "bomb" },
      { mark: "🚂", name: "railway" },
      { mark: "⛽", name: "fuel" },
      { mark: "🌾", name: "grain" },
      { mark: "⚓", name: "navy" },
      { mark: "✈️", name: "air" },
      { mark: "🧭", name: "bearing" },
      { mark: "🗺️", name: "the map" },
    ],
  },
  {
    name: "Signs",
    marks: [
      { mark: "❓", name: "question" },
      { mark: "❗", name: "emphasis" },
      { mark: "⚠️", name: "caution" },
      { mark: "✅", name: "done" },
      { mark: "❌", name: "no" },
      { mark: "⭕", name: "yes" },
      { mark: "🔴", name: "red" },
      { mark: "🟡", name: "amber" },
      { mark: "🟢", name: "green" },
      { mark: "⏳", name: "waiting" },
      { mark: "⌛", name: "out of time" },
      { mark: "🔒", name: "sealed" },
      { mark: "🔓", name: "open" },
      { mark: "📜", name: "treaty" },
      { mark: "📈", name: "rising" },
      { mark: "📉", name: "falling" },
    ],
  },
];

/**
 * One emoji. Hermes, which runs the iOS app, has no `v` flag, so there a `u` pattern accepts the
 * same shapes: flags, keycaps, skin tones, and pictographs joined into one by zero-width joiners or
 * followed by subdivision tags.
 */
const singleEmoji = (): RegExp => {
  try {
    // biome-ignore lint/complexity/useRegexLiterals: a `v` flag literal needs an ES2024 compile target.
    return new RegExp("^\\p{RGI_Emoji}$", "v");
  } catch {
    return /^(?:\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|\p{Emoji_Modifier}|\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})?)*(?:[\u{E0020}-\u{E007E}]+\u{E007F})?)$/u;
  }
};

/** Built on first use: V8 takes about 10 ms to build the pattern, which every importer would pay. */
let singleEmojiPattern: RegExp | undefined;
const MAX_REACTION_MARK_LENGTH = 32;

export function isReactionMark(text: string): boolean {
  if (text.length > MAX_REACTION_MARK_LENGTH) return false;
  singleEmojiPattern ??= singleEmoji();
  return singleEmojiPattern.test(text);
}

export const ReactionMarkSchema = z
  .string()
  .max(MAX_REACTION_MARK_LENGTH)
  .refine(isReactionMark, "A reaction mark is a single emoji.")
  .meta({ description: "A single emoji, such as 👍 or ❓." });

/**
 * What a mark is called, for a control that is otherwise only a picture. Marks
 * outside the suggested set are their own name, which screen readers speak.
 */
export function reactionName(mark: string): string {
  for (const group of SUGGESTED_REACTIONS) {
    const found = group.marks.find(candidate => candidate.mark === mark);
    if (found) return found.name;
  }
  return mark;
}

export const PollResponseSchema = z.union([
  z.strictObject({ optionId: IdSchema }),
  z.strictObject({ text: z.string().trim().min(1).max(20_000) }),
]);
export type PollResponse = z.infer<typeof PollResponseSchema>;

const MembersSchema = listOf(ThreadMemberSchema).refine(
  members => new Set(members.map(member => member.id)).size === members.length,
  "Member ids must be unique.",
);

/** One change to a thread's members. A removed member stays in the thread and can still speak. */
export const ThreadMemberChangeSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("append"), member: ThreadMemberSchema }),
  z.strictObject({
    type: z.literal("update"),
    id: IdSchema,
    changes: z.strictObject({
      name: NameSchema.optional(),
      role: NameSchema.nullable().optional(),
      image: AssetVersionIdSchema.nullable().optional(),
      color: HexColorSchema.nullable().optional(),
    }),
  }),
  z.strictObject({ type: z.literal("remove"), id: IdSchema }),
]);
export type ThreadMemberChange = z.infer<typeof ThreadMemberChangeSchema>;

export function memberChangeId(change: ThreadMemberChange): string {
  return change.type === "append" ? change.member.id : change.id;
}

export const ThreadOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("thread.create"),
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
    thread: z.strictObject({
      id: IdSchema,
      title: NameSchema,
      members: MembersSchema,
      /** What the conversation is about on the map, which the map fits beside it when it opens. */
      frame: ExtentSchema.optional(),
    }),
  }),
  z.strictObject({
    type: z.literal("thread.append"),
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
    threadId: IdSchema,
    entry: ThreadEntrySchema,
  }),
  z.strictObject({
    type: z.literal("thread.update"),
    id: IdSchema,
    changes: z.strictObject({
      title: NameSchema.optional(),
      status: ThreadStatusSchema.optional(),
      frame: ExtentSchema.nullable().optional(),
      members: listOf(ThreadMemberChangeSchema)
        .refine(changes => {
          const ids = changes.map(memberChangeId);
          return new Set(ids).size === ids.length;
        }, "No member id may appear in more than one change.")
        .optional(),
    }),
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
  }),
  z.strictObject({
    type: z.literal("thread.pollResponded"),
    threadId: IdSchema,
    pollId: IdSchema,
    respondent: ThreadSpeakerSchema,
    response: PollResponseSchema,
  }),
  z.strictObject({ type: z.literal("thread.pollClosed"), threadId: IdSchema, pollId: IdSchema }),
  z.strictObject({
    type: z.literal("thread.reactionSet"),
    threadId: IdSchema,
    entryId: IdSchema.meta({ description: "A message in the thread." }),
    reactor: ThreadSpeakerSchema,
    mark: ReactionMarkSchema,
  }),
  z.strictObject({
    type: z.literal("thread.reactionUnset"),
    threadId: IdSchema,
    entryId: IdSchema,
    reactor: ThreadSpeakerSchema,
    mark: ReactionMarkSchema,
  }),
  z.strictObject({
    type: z.literal("thread.pinsSet"),
    threadIds: listOf(IdSchema)
      .max(MAX_PINNED_THREADS)
      .refine(ids => new Set(ids).size === ids.length, "Pinned thread ids must be unique."),
  }),
]);
export type ThreadOperation = z.infer<typeof ThreadOperationSchema>;
